import React, { useMemo, useState } from 'react';
import { Config, ImmutableTree, Utils } from '@react-awesome-query-builder/core';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { CustomKeyAttributes } from '@loyalty/shared-common/profile-api-client';
import { EventName, EventSource, Feature } from '@loyalty/shared-common';
import { useQlfTranslation } from '@qualifio/ui-commons';
import { useTranslation } from 'react-i18next';
import { AccessRuleFormValue } from '../../../earning-rules/form';
import {
  BaseConfigSettings,
  configureFieldsForAccessRules,
} from '../../../rules/components/query-builder/config/fields';
import { RulesQueryBuilder, initialConfig } from '../../../rules/components';
import { useAccounts } from '../../../accounts/AccountsContext';
import { useCustomKeys } from '../../../custom-keys/CustomKeysContext';
import { useFeatures } from '../../../features/FeaturesContext';

const { checkTree, loadFromJsonLogic } = Utils;

function makeTree(jsonLogic: any, conf: Config): ImmutableTree {
  const defaultValue = { and: [] };
  const tree = loadFromJsonLogic(jsonLogic || defaultValue, conf);
  if (!tree) {
    throw new Error('Could not load tree from jsonLogic');
  }
  return checkTree(tree, conf);
}

function makeConfig(
  settings: BaseConfigSettings,
  readOnly = false,
  customKeys?: CustomKeyAttributes[],
) {
  return {
    ...initialConfig(settings.t, readOnly),
    fields: configureFieldsForAccessRules(settings, customKeys),
  };
}

interface AccessQueryBuilderProps {
  value: any;
  onChange: (value: any) => void;
  error?: FieldError;
}

function AccessQueryBuilder({
  value,
  onChange,
  error,
}: AccessQueryBuilderProps): React.ReactElement {
  const { currentAccount } = useAccounts();
  const { t, i18n } = useTranslation();
  const { t: htmlT } = useQlfTranslation('translation');
  const { features } = useFeatures();
  const { write: accessRuleWrite } = features[Feature.PROGRAM_ACCESS];
  const settings = useMemo<BaseConfigSettings>(
    () => ({
      eventSource: EventSource.QUALIFIO_LOYALTY,
      eventName: EventName.ACCESS,
      locale: i18n.language,
      t,
    }),
    [i18n.language],
  );
  const { customKeys } = useCustomKeys();

  const config = useMemo(
    () => makeConfig({ ...settings }, !accessRuleWrite, customKeys),
    [settings, accessRuleWrite, customKeys],
  );
  const [tree] = useState(makeTree(value, config));

  return (
    <RulesQueryBuilder
      error={error?.message}
      tree={tree}
      onChange={(immutableTree, conf, newVal) => {
        onChange(newVal);
      }}
      config={config}
      label={t('access-rules.properties.criteria')}
      description={htmlT('access-rules.criteria.description', {
        customKeysUrl: `/accounts/${currentAccount.id}/keys`,
      })}
    />
  );
}

export function AccessCriteriaForm(): React.ReactElement {
  const { control } = useFormContext<AccessRuleFormValue>();

  return (
    <div style={{ marginTop: '30px' }}>
      <Controller
        control={control}
        name="criteria"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <AccessQueryBuilder
            value={value}
            onChange={val => onChange({ target: { value: val } })}
            error={error}
          />
        )}
      />
    </div>
  );
}
